import {gsap} from "../../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras';
import {AppearingInteractiveButton} from "./AppearingInteractiveButton";

// Constructor
export class GameG_Dropzone extends PIXI.Container {

  constructor(position, matchData, game) {
    super();

    this.game = game;
    this.matchData = matchData;
    this.id = this.matchData.id;
    this.descriptionShown = false;
    this.isFilled = false;
    this.droppedInZone = false;
    this.pluseTl = null;

    this.originalPosition = new PIXI.Point(position.x, position.y);
    this.position.copyFrom(this.originalPosition);

    // Title text style
    this.titleTextStyle = new PIXI.HTMLTextStyle({
      align: "center",
      fill: "#00156B",
      fontFamily: "SketchnoteTextBold",
      fontSize: 16,
      fontWeight: "bold",
      lineHeight: 16 * 1.24, // required to set lineHeight otherwise the text is cropped
      // wordWrap: true,
      // wordWrapWidth: 520 - (35 * 2) // line break after X pixels
    });

    // Description text style
    this.textStyle = new PIXI.HTMLTextStyle({
      align: "center",
      fill: "#00156B",
      fontFamily: "Sketchnotetext Regular",
      fontSize: 20,
      lineHeight: 20 * 1.24, // required to set lineHeight otherwise the text is cropped
      wordWrap: true,
      wordWrapWidth: 520 - (35 * 2),// line break after X pixels
      tagStyles: {
        b: {fontFamily: 'SketchnoteTextBold'}
      }
    });

    this.build();
  }

  build() {
    // Background
    const matchBg = new PIXI.Sprite(this.game.assets["match-background"]);
    matchBg.width = 520;
    matchBg.height = 386;
    this.addChild(matchBg);

    // Match target image
    const matchTargetSprite = new PIXI.Sprite(this.game.assets[this.matchData["image"]]);
    matchTargetSprite.width = 216;
    matchTargetSprite.height = 205;
    matchTargetSprite.position.x = 35;
    matchTargetSprite.position.y = (matchBg.height - matchTargetSprite.height) / 2;
    this.addChild(matchTargetSprite)

    // Dropzone container
    this.dropZone = new PIXI.Container();
    const matchDropSprite = new PIXI.Sprite(this.game.assets["match-dropzone"]);
    matchDropSprite.width = 216;
    matchDropSprite.height = 205;
    this.dropZone.position.x = matchTargetSprite.width + matchTargetSprite.position.x + 20;
    this.dropZone.position.y = matchTargetSprite.position.y;
    this.dropZone.addChild(matchDropSprite);
    this.addChild(this.dropZone);

    // Description
    this.description = new PIXI.Container();
    this.description.alpha = 0.0;

    // Description background
    const descriptionBg = new PIXI.Sprite(this.game.assets["description-background"]);
    descriptionBg.width = 520;
    descriptionBg.height = 386;
    this.description.addChild(descriptionBg);

    // Title text
    const titleContainer = new PIXI.Container();
    const titleEN = this.game.createInterfaceHTMLText(this.matchData.title.en.toUpperCase(), this.titleTextStyle, "en");
    const titleFR = this.game.createInterfaceHTMLText(this.matchData.title.fr.toUpperCase(), this.titleTextStyle, "fr");
    const titleBackgroundWidth = Math.min(Math.max(titleEN.width, titleFR.width) + 100, 460);
    const titleBackground = new PIXI.Graphics().roundRect(0, 0, titleBackgroundWidth, 48, 20).fill(0xA8C5EF);

    titleContainer.position.x = (this.description.width - titleBackgroundWidth) / 2;
    titleContainer.position.y = 40;
    titleEN.position.set((titleBackgroundWidth - titleEN.width) / 2, 15);
    titleFR.position.set((titleBackgroundWidth - titleFR.width) / 2, 15);

    titleContainer.addChild(titleBackground);
    titleContainer.addChild(titleEN);
    titleContainer.addChild(titleFR);
    this.description.addChild(titleContainer);

    // Description text
    const textEN = this.game.createInterfaceHTMLText(this.matchData.text.en, this.textStyle, "en");
    const textFR = this.game.createInterfaceHTMLText(this.matchData.text.fr, this.textStyle, "fr");
    textEN.position.set(35, 60 + (460 - 125 - textEN.height) / 2 );
    textFR.position.set(35, 60 +  (460 - 125 - textFR.height) / 2);
    this.description.addChild(textEN);
    this.description.addChild(textFR);
    this.addChild(this.description);

    // Exclamation button
    const exclamation = new PIXI.Sprite(this.game.assets["exclamation"]);
    this.openButton = new AppearingInteractiveButton(60, 60, exclamation, false, this.openDescription.bind(this), false);
    this.openButton.position.x = this.width - 10;
    this.openButton.position.y = 10;
    this.openButton.show();
    this.openButton.eventMode = "none";
    this.openButton.cursor = "default";
    this.addChild(this.openButton);

    // Close button
    const cross = new PIXI.Sprite(this.game.assets["cross"]);
    this.closeButton = new AppearingInteractiveButton(60, 60, cross, false, this.closeDescription.bind(this), false);
    this.closeButton.position.x = this.width - 10;
    this.closeButton.position.y = 10;
    this.closeButton.eventMode = "none";
    this.closeButton.cursor = "default";
    this.addChild(this.closeButton);

    this.pulse();
  }

  enable() {
    //this.dropZone.eventMode = "static";
  }

  animateDropped() {
    gsap.killTweensOf(this);

    gsap.to(this.dropZone, {
      pixi: {
        alpha: 0,
      }, ease: "quad.out", duration: 0.25,
    });
  }

  animateReset(duration = 0.25) {
    gsap.killTweensOf(this.dropZone);

    this.pulse();

    gsap.to(this.dropZone, {
      pixi: {
        alpha: 1,
      }, ease: "quad.out", duration: duration,
    });
  }

  openDescription() {
    this.descriptionShown = true;

    gsap.to(this.description, {
      alpha: 1.0, duration: 0.25, ease: "power1.inOut"
    });
    if (this.isFilled && this.droppedInZone) {
      gsap.to(this.droppedInZone, {
        alpha: 0.0, duration: 0.25, ease: "power1.inOut"
      });
    }

    this.openButton.hide();
    this.closeButton.show();

    // Stop pulse for all dropzones
    for (let i = 0; i < this.game.dropzoneObjects.length; i++) {
      this.game.dropzoneObjects[i].stopPulse();
    }
  }

  closeDescription() {
    this.descriptionShown = false;

    gsap.to(this.description, {
      alpha: 0.0, duration: 0.25, ease: "power1.inOut"
    });
    if (this.isFilled && this.droppedInZone) {
      gsap.to(this.droppedInZone, {
        alpha: 1.0, duration: 0.25, ease: "power1.inOut"
      });
    }

    this.closeButton.hide();
    this.openButton.show();
  }

  getBoundingBox() {
    let dropZoneGlobalPos = this.dropZone.toGlobal(new PIXI.Point(0, 0));

    return {
      x: dropZoneGlobalPos.x,
      y: dropZoneGlobalPos.y,
      width: this.dropZone.width,
      height: this.dropZone.height,
      top: dropZoneGlobalPos.y,
      right: dropZoneGlobalPos.x + this.dropZone.width,
      bottom: dropZoneGlobalPos.y + this.dropZone.height,
      left: dropZoneGlobalPos.x
    };
  }

  resetState(duration) {
    this.closeDescription();
    this.isFilled = false;

    this.animateReset(duration);
  }

  pulse() {
    this.pluseTl = gsap.fromTo(this.openButton, {
      pixi: {
        scale: 1,
      },
    }, {
      pixi: {
        scale: 0.9,
      },
      ease: "quad.out",
      duration: 0.5,
      yoyo: true,
      repeat: -1,
    });
  }

  stopPulse() {
    this.pluseTl.kill();
  }

  // Reset the states so we can animate in again and start over
  reset() {
    if (this.inAnimation) {
      this.inAnimation.kill();
      this.inAnimation = null;
    }

    this.position.copyFrom(this.originalPosition);

    this.descriptionShown = false;
    this.isFilled = false;
    this.droppedInZone = false;

    this.animateReset(0);
    this.description.alpha = 0.0;
    this.closeButton.hide(0);
    this.openButton.show();
  }
}
